/**
 * useInvoices Hook
 * React Query hooks for invoice operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/core/api/client';
import {
  getInvoices,
  getInvoiceById,
  generateSingleInvoice,
  generateMonthlyInvoice,
  createCustomInvoice,
  finalizeInvoice,
  validateInvoice,
  cancelInvoice,
  markInvoiceAsPaid,
  resyncInvoiceFalco,
  checkSingleInvoiceEligibility,
  getMonthlyBillingEligible,
  getMonthlyBillingContacts,
  getAffaireMonthlyBillingTracks,
  getClientBillingEligible,
  getSequenceStatus,
  peekNextInvoiceNumber
} from '../api/facturation.api';
import type { ValidateInvoiceOptions } from '../api/facturation.api';
import type { Invoice, InvoiceFilters, InvoiceStatus, PaginatedResponse, CreateCustomInvoiceData } from '../types/facturation.types';
import { validateInvoiceOperation } from '../types/facturation.types';

// API Response wrapper type for invoice detail
interface ApiResponse<T> {
  success: boolean;
  data: T;
}

// Retry configuration for mutations with exponential backoff
const MUTATION_RETRY_CONFIG = {
  retry: 3,
  retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000), // 1s, 2s, 4s... max 30s
};

// Query keys
export const invoiceKeys = {
  all: ['invoices'] as const,
  lists: () => [...invoiceKeys.all, 'list'] as const,
  list: (filters: InvoiceFilters) => [...invoiceKeys.lists(), filters] as const,
  details: () => [...invoiceKeys.all, 'detail'] as const,
  detail: (id: number) => [...invoiceKeys.details(), id] as const,
  eligibility: (affaireId: string) => [...invoiceKeys.all, 'eligibility', affaireId] as const,
  monthlyEligible: (billingContactId: string) => [...invoiceKeys.all, 'monthly-eligible', billingContactId] as const,
  monthlyContacts: () => [...invoiceKeys.all, 'monthly-contacts'] as const,
  affaireMonthlyTracks: (affaireId: string) => [...invoiceKeys.all, 'affaire-monthly-tracks', affaireId] as const,
  contactInvoices: (billingContactId: string) => [...invoiceKeys.all, 'contact-invoices', billingContactId] as const,
  clientEligible: (clientId: string) => [...invoiceKeys.all, 'client-eligible', clientId] as const,
  sequence: (year?: number) => [...invoiceKeys.all, 'sequence', year] as const,
  nextNumber: (year?: number, type?: string) => [...invoiceKeys.all, 'next-number', year, type] as const,
};

// Get paginated invoices
export const useInvoices = (filters: InvoiceFilters = {}) => {
  return useQuery({
    queryKey: invoiceKeys.list(filters),
    queryFn: () => getInvoices(filters),
    staleTime: 30 * 1000, // 30 seconds - shorter to ensure fresher data
    refetchOnWindowFocus: true,
  });
};

// Get single invoice by ID
export const useInvoice = (id: number) => {
  return useQuery({
    queryKey: invoiceKeys.detail(id),
    queryFn: () => getInvoiceById(id),
    enabled: !!id,
  });
};

// Check single invoice eligibility
export const useInvoiceEligibility = (affaireId: string) => {
  return useQuery({
    queryKey: invoiceKeys.eligibility(affaireId),
    queryFn: () => checkSingleInvoiceEligibility(affaireId),
    enabled: !!affaireId,
    staleTime: 0, // Always refetch on mount for fresh affaire data
  });
};

// Get monthly billing eligible affaires
export const useMonthlyBillingEligible = (billingContactId: string) => {
  return useQuery({
    queryKey: invoiceKeys.monthlyEligible(billingContactId),
    queryFn: () => getMonthlyBillingEligible(billingContactId),
    enabled: !!billingContactId,
    staleTime: 0, // Always refetch on mount for fresh affaire data
  });
};

// Get the FACTURE MENSUELLE billing track(s) attached to a single affaire.
// Used by ProductsEmbeddedView to surface the monthly billing context per
// product on mixed-payer affaires.
export const useAffaireMonthlyTracks = (affaireId: string | null | undefined) => {
  return useQuery({
    queryKey: invoiceKeys.affaireMonthlyTracks(affaireId ?? ''),
    queryFn: () => getAffaireMonthlyBillingTracks(affaireId as string),
    enabled: !!affaireId,
    staleTime: 60 * 1000,
  });
};

// Get all monthly billing contacts with eligible affaires (for Group Facturation tab)
export const useMonthlyBillingContacts = () => {
  return useQuery({
    queryKey: invoiceKeys.monthlyContacts(),
    queryFn: () => getMonthlyBillingContacts(),
    staleTime: 30 * 1000, // 30 seconds - shorter to ensure fresher data
    refetchOnWindowFocus: true,
  });
};

// Get invoices for a specific billing contact (for Group Facturation invoice history)
export const useContactInvoices = (billingContactId: string) => {
  return useQuery({
    queryKey: invoiceKeys.contactInvoices(billingContactId),
    queryFn: () => getInvoices({ billingContactId, limit: 50 }),
    enabled: !!billingContactId,
    staleTime: 0, // Always refetch on mount for fresh invoice data
  });
};

// Get client billing eligible affaires (grouped by billing contact)
export const useClientBillingEligible = (clientId: string) => {
  return useQuery({
    queryKey: invoiceKeys.clientEligible(clientId),
    queryFn: () => getClientBillingEligible(clientId),
    enabled: !!clientId,
    staleTime: 0, // Always refetch on mount for fresh affaire data
  });
};

// Get sequence status
export const useSequenceStatus = (year?: number) => {
  return useQuery({
    queryKey: invoiceKeys.sequence(year),
    queryFn: () => getSequenceStatus(year),
  });
};

// Peek next invoice number
export const useNextInvoiceNumber = (year?: number, type: 'INVOICE' | 'CREDIT_NOTE' = 'INVOICE') => {
  return useQuery({
    queryKey: invoiceKeys.nextNumber(year, type),
    queryFn: () => peekNextInvoiceNumber(year, type),
  });
};

// Generate single invoice mutation
export const useGenerateSingleInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      affaireId,
      language,
      billingContactId,
      selectedProductIds,
      productSelections,
      discount,
      viesOfficialData,
      bankAccountId,
      billingAddressOverride,
      reverseCharge
    }: {
      affaireId: string;
      language?: string;
      billingContactId?: string;  // Optional: specific contact when multiple FACTURE contacts exist
      selectedProductIds?: number[];  // Legacy: simple product selection
      productSelections?: Array<{ productId: number; quantity: number }>;  // New: with custom quantities
      discount?: {
        code: string;
        type: 'percentage' | 'fixed';
        value: number;
        description?: string;
      };
      viesOfficialData?: {  // VIES official data for professional customers
        name?: string | null;
        address?: string | null;
      };
      bankAccountId?: number;
      billingAddressOverride?: {  // Operator-edited billing address (overrides FM/VIES)
        address?: string | null;
        postalCode?: string | null;
        city?: string | null;
        countryCode?: string | null;
      };
      reverseCharge?: boolean;  // Intra-EU reverse charge (autoliquidation)
    }) => generateSingleInvoice(affaireId, { language, billingContactId, selectedProductIds, productSelections, discount, viesOfficialData, bankAccountId, billingAddressOverride, reverseCharge }),
    ...MUTATION_RETRY_CONFIG,
    onSuccess: (_, { affaireId }) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.sequence() });
      // Also invalidate eligibility for this affaire since products have now been invoiced
      queryClient.invalidateQueries({ queryKey: invoiceKeys.eligibility(affaireId) });
    },
  });
};

// Generate monthly invoice mutation
export const useGenerateMonthlyInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      affaireIds,
      billingContactId,
      language,
      viesOfficialData,
      bankAccountId
    }: {
      affaireIds: string[];
      billingContactId: string;
      language?: string;
      viesOfficialData?: {  // VIES official data for professional customers
        name?: string | null;
        address?: string | null;
      };
      bankAccountId?: number;
    }) =>
      generateMonthlyInvoice(affaireIds, billingContactId, { language, viesOfficialData, bankAccountId }),
    ...MUTATION_RETRY_CONFIG,
    onSuccess: (_, { billingContactId }) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.sequence() });
      // Also invalidate monthly eligibility since these affaires have now been invoiced
      queryClient.invalidateQueries({ queryKey: invoiceKeys.monthlyEligible(billingContactId) });
    },
  });
};

/**
 * Create custom invoice mutation - for invoices not linked to any affaire
 * Used for one-time customers or services outside of normal affaire workflow
 */
export const useCreateCustomInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCustomInvoiceData) => createCustomInvoice(data),
    ...MUTATION_RETRY_CONFIG,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.sequence() });
    },
  });
};

/**
 * @deprecated Use useValidateInvoice instead.
 * Generate now creates PDF immediately, validate uploads to Falco.
 */
export const useFinalizeInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => finalizeInvoice(id),
    ...MUTATION_RETRY_CONFIG,
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
    },
  });
};

/**
 * Validate invoice mutation - uploads to Falco/Horus and marks as SENT (locked)
 * This is step 2 of the new workflow:
 * 1. Generate invoice → PDF created → status GENERATED
 * 2. Validate invoice → Falco upload → status SENT (cannot delete)
 *
 * Optionally sends invoice by email to billing contact:
 * - sendEmail: whether to send email (default false)
 * - emailMessage: custom message to include in email
 * - emailRecipient: override recipient for testing
 */
export const useValidateInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      currentStatus,
      sendEmail,
      emailMessage,
      emailRecipient,
      language
    }: {
      id: number;
      currentStatus?: InvoiceStatus;
    } & ValidateInvoiceOptions) => {
      // Client-side status validation (if status is provided)
      if (currentStatus) {
        const validation = validateInvoiceOperation(currentStatus, 'validate');
        if (!validation.valid) {
          return Promise.reject(new Error(validation.error));
        }
      }

      // Build email options if any are provided
      const emailOptions: ValidateInvoiceOptions | undefined =
        sendEmail !== undefined || emailMessage || emailRecipient || language
          ? { sendEmail, emailMessage, emailRecipient, language }
          : undefined;

      return validateInvoice(id, emailOptions);
    },
    ...MUTATION_RETRY_CONFIG,
    onMutate: async ({ id }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: invoiceKeys.detail(id) });
      await queryClient.cancelQueries({ queryKey: invoiceKeys.lists() });

      // Snapshot previous values
      const previousInvoice = queryClient.getQueryData(invoiceKeys.detail(id));
      const previousLists = queryClient.getQueriesData({ queryKey: invoiceKeys.lists() });

      // Optimistically update detail view
      queryClient.setQueryData<ApiResponse<Invoice>>(invoiceKeys.detail(id), (old) => {
        if (!old?.data) return old;
        return {
          ...old,
          data: {
            ...old.data,
            status: 'SENT',
            falco_status: 'PENDING',
          },
        };
      });

      // Optimistically update list views
      queryClient.setQueriesData<PaginatedResponse<Invoice>>({ queryKey: invoiceKeys.lists() }, (old) => {
        if (!old?.invoices) return old;
        return {
          ...old,
          invoices: old.invoices.map((inv) =>
            inv.id === id ? { ...inv, status: 'SENT', falco_status: 'PENDING' } : inv
          ),
        };
      });

      // Return context with snapshots for rollback
      return { previousInvoice, previousLists, id };
    },
    onError: (err, _, context) => {
      // Rollback on error
      if (context?.previousInvoice && context?.id) {
        queryClient.setQueryData(invoiceKeys.detail(context.id), context.previousInvoice);
      }
      if (context?.previousLists) {
        context.previousLists.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      console.error('Invoice validation failed:', err);
    },
    onSettled: (_, __, { id }) => {
      // Always refetch after error or success to ensure consistency
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
    },
  });
};

// Cancel invoice mutation
export const useCancelInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason, affaireIds, currentStatus }: { id: number; reason: string; affaireIds?: string[]; currentStatus?: InvoiceStatus }) => {
      // Client-side status validation (if status is provided)
      if (currentStatus) {
        const validation = validateInvoiceOperation(currentStatus, 'cancel');
        if (!validation.valid) {
          return Promise.reject(new Error(validation.error));
        }
      }
      return cancelInvoice(id, reason);
    },
    ...MUTATION_RETRY_CONFIG,
    onMutate: async ({ id }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: invoiceKeys.detail(id) });
      await queryClient.cancelQueries({ queryKey: invoiceKeys.lists() });

      // Snapshot previous values
      const previousInvoice = queryClient.getQueryData(invoiceKeys.detail(id));
      const previousLists = queryClient.getQueriesData({ queryKey: invoiceKeys.lists() });

      // Optimistically update detail view
      queryClient.setQueryData<ApiResponse<Invoice>>(invoiceKeys.detail(id), (old) => {
        if (!old?.data) return old;
        return {
          ...old,
          data: {
            ...old.data,
            status: 'CANCELLED',
          },
        };
      });

      // Optimistically update list views
      queryClient.setQueriesData<PaginatedResponse<Invoice>>({ queryKey: invoiceKeys.lists() }, (old) => {
        if (!old?.invoices) return old;
        return {
          ...old,
          invoices: old.invoices.map((inv) =>
            inv.id === id ? { ...inv, status: 'CANCELLED' } : inv
          ),
        };
      });

      // Return context with snapshots for rollback
      return { previousInvoice, previousLists, id };
    },
    onError: (err, _, context) => {
      // Rollback on error
      if (context?.previousInvoice && context?.id) {
        queryClient.setQueryData(invoiceKeys.detail(context.id), context.previousInvoice);
      }
      if (context?.previousLists) {
        context.previousLists.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      console.error('Invoice cancellation failed:', err);
    },
    onSettled: (_, __, { id, affaireIds }) => {
      // Always refetch after error or success to ensure consistency
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      // Invalidate eligibility for affected affaires so products become available again
      if (affaireIds && affaireIds.length > 0) {
        affaireIds.forEach(affaireId => {
          queryClient.invalidateQueries({ queryKey: invoiceKeys.eligibility(affaireId) });
        });
      } else {
        // If no affaireIds provided, invalidate all eligibility queries
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            query.queryKey.includes('eligibility')
        });
      }
    },
  });
};

// Mark invoice as paid mutation
export const useMarkInvoiceAsPaid = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, paymentDate, paymentReference, currentStatus }: { id: number; paymentDate?: string; paymentReference?: string; currentStatus?: InvoiceStatus }) => {
      // Client-side status validation (if status is provided)
      if (currentStatus) {
        const validation = validateInvoiceOperation(currentStatus, 'markPaid');
        if (!validation.valid) {
          return Promise.reject(new Error(validation.error));
        }
      }
      return markInvoiceAsPaid(id, paymentDate, paymentReference);
    },
    ...MUTATION_RETRY_CONFIG,
    onMutate: async ({ id, paymentDate }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: invoiceKeys.detail(id) });
      await queryClient.cancelQueries({ queryKey: invoiceKeys.lists() });

      // Snapshot previous values
      const previousInvoice = queryClient.getQueryData(invoiceKeys.detail(id));
      const previousLists = queryClient.getQueriesData({ queryKey: invoiceKeys.lists() });

      // Optimistically update detail view
      queryClient.setQueryData<ApiResponse<Invoice>>(invoiceKeys.detail(id), (old) => {
        if (!old?.data) return old;
        return {
          ...old,
          data: {
            ...old.data,
            status: 'PAID',
            payment_date: paymentDate || new Date().toISOString(),
          },
        };
      });

      // Optimistically update list views
      queryClient.setQueriesData<PaginatedResponse<Invoice>>({ queryKey: invoiceKeys.lists() }, (old) => {
        if (!old?.invoices) return old;
        return {
          ...old,
          invoices: old.invoices.map((inv) =>
            inv.id === id
              ? { ...inv, status: 'PAID', payment_date: paymentDate || new Date().toISOString() }
              : inv
          ),
        };
      });

      // Return context with snapshots for rollback
      return { previousInvoice, previousLists, id };
    },
    onError: (err, _, context) => {
      // Rollback on error
      if (context?.previousInvoice && context?.id) {
        queryClient.setQueryData(invoiceKeys.detail(context.id), context.previousInvoice);
      }
      if (context?.previousLists) {
        context.previousLists.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      console.error('Mark invoice as paid failed:', err);
    },
    onSettled: (_, __, { id }) => {
      // Always refetch after error or success to ensure consistency
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
    },
  });
};

// Resync Falco mutation
export const useResyncFalco = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, currentStatus }: { id: number; currentStatus?: InvoiceStatus }) => {
      // Client-side status validation (if status is provided)
      if (currentStatus) {
        const validation = validateInvoiceOperation(currentStatus, 'resyncFalco');
        if (!validation.valid) {
          return Promise.reject(new Error(validation.error));
        }
      }
      return resyncInvoiceFalco(id);
    },
    ...MUTATION_RETRY_CONFIG,
    onMutate: async ({ id }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: invoiceKeys.detail(id) });
      await queryClient.cancelQueries({ queryKey: invoiceKeys.lists() });

      // Snapshot previous values
      const previousInvoice = queryClient.getQueryData(invoiceKeys.detail(id));
      const previousLists = queryClient.getQueriesData({ queryKey: invoiceKeys.lists() });

      // Optimistically update detail view - set falco status to pending
      queryClient.setQueryData<ApiResponse<Invoice>>(invoiceKeys.detail(id), (old) => {
        if (!old?.data) return old;
        return {
          ...old,
          data: {
            ...old.data,
            falco_status: 'PENDING',
            falco_error: null,
          },
        };
      });

      // Optimistically update list views
      queryClient.setQueriesData<PaginatedResponse<Invoice>>({ queryKey: invoiceKeys.lists() }, (old) => {
        if (!old?.invoices) return old;
        return {
          ...old,
          invoices: old.invoices.map((inv) =>
            inv.id === id ? { ...inv, falco_status: 'PENDING', falco_error: null } : inv
          ),
        };
      });

      // Return context with snapshots for rollback
      return { previousInvoice, previousLists, id };
    },
    onError: (err, _, context) => {
      // Rollback on error
      if (context?.previousInvoice && context?.id) {
        queryClient.setQueryData(invoiceKeys.detail(context.id), context.previousInvoice);
      }
      if (context?.previousLists) {
        context.previousLists.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      console.error('Falco resync failed:', err);
    },
    onSettled: (_, __, { id }) => {
      // Always refetch after error or success to ensure consistency
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
    },
  });
};

// ======== Invoice Payment Details ========

export interface InvoicePaymentDetail {
  payment_id: number;
  date: string;
  debtor_name: string;
  debtor_iban: string | null;
  payment_total_amount: string;
  amount_allocated: string;
  affaire_id: string;
  is_validated: boolean;
  matching_method: string | null;
  linked_at: string | null;
}

export const useInvoicePayments = (invoiceId: number, enabled: boolean) => {
  return useQuery<InvoicePaymentDetail[]>({
    queryKey: ['invoice-payments', invoiceId],
    queryFn: async () => {
      // apiClient interceptor already unwraps response.data,
      // so result is { success, data } directly
      const result = await apiClient.get<{ success: boolean; data: InvoicePaymentDetail[] }>(`/api/billing/invoices/${invoiceId}/payments`);
      return (result as any)?.data || [];
    },
    enabled,
    staleTime: 30_000,
  });
};
